import { isPdfBlob,printPdfBlob } from "./printPdfDocument";

const PRINT_LOAD_TIMEOUT_MS = 20000;
const PRINT_CLEANUP_DELAY_MS = 5000;
const PRINT_LAYOUT_SETTLE_MS = 250;

export type ReservedPersonDocumentPrintWindow = Window;

export function reservePersonDocumentPrintWindow(preparingText?: string): ReservedPersonDocumentPrintWindow | null {
  const printWindow = window.open("about:blank", "_blank");
  if (!printWindow) return null;

  try {
    writePrintShell(printWindow, preparingText);
  } catch {
    // Some browsers may restrict access immediately after opening; navigation fallback still works.
  }

  return printWindow;
}

export function closeReservedPersonDocumentPrintWindow(printWindow: ReservedPersonDocumentPrintWindow | null | undefined): void {
  try {
    printWindow?.close();
  } catch {
    // Browser may deny closing a window if it was already navigated or handled externally.
  }
}

export async function printPersonDocument({
  personId,
  documentId,
  loadDocumentBlob,
  printWindow,
  preparingText,
  documentTitle,
  onPrintFailed
}: {
  personId: string;
  documentId: string;
  loadDocumentBlob: (url: string) => Promise<Blob>;
  printWindow?: ReservedPersonDocumentPrintWindow | null;
  preparingText?: string;
  /** Titel des Druckdokuments — wird bei PDFs zum Standard-Dateinamen im Druckdialog. */
  documentTitle?: string;
  onPrintFailed?: () => void;
}): Promise<boolean> {
  const url = `/api/v1/persons/${encodeURIComponent(personId)}/documents/${encodeURIComponent(documentId)}?inline=true`;

  try {
    const blob = await loadPrintableDocumentBlob(url, loadDocumentBlob);

    // PDFs koennen nicht ueber ein iframe gedruckt werden — sie laufen ueber den
    // pdf.js-Weg in `printPdfDocument`. Alles andere (Bilder, HTML) bleibt beim iframe.
    if (await isPdfBlob(blob)) {
      closeReservedPersonDocumentPrintWindow(printWindow);
      const printed = await printPdfBlob(blob, { title: documentTitle || "" });
      if (!printed) onPrintFailed?.();
      return printed;
    }

    const objectUrl = URL.createObjectURL(blob);
    const documentContext = { url, contentType: blob.type, size: blob.size };
    const frameOwner = printWindow ?? window;
    const frameDocument = frameOwner.document;
    if (printWindow) {
      writePrintShell(printWindow, preparingText);
    }
    const frame = printWindow
      ? frameDocument.getElementById("person-document-print-frame") as HTMLIFrameElement | null
      : createHiddenPrintFrame(frameDocument);
    if (!frame) {
      URL.revokeObjectURL(objectUrl);
      throw new Error("Print frame not available.");
    }

    let failed = false;
    const failTimer = frameOwner.setTimeout(() => {
      failed = true;
      URL.revokeObjectURL(objectUrl);
      if (!printWindow) frame.remove();
      // Ohne diese Ausgabe ist am Fehler-Toast nicht erkennbar, dass das Dokument im
      // Druckrahmen nie fertig geladen wurde.
      console.error("Dokument konnte nicht zum Drucken geladen werden (Zeitueberschreitung)", documentContext);
      onPrintFailed?.();
    }, PRINT_LOAD_TIMEOUT_MS);

    frame.onload = () => {
      if (failed) return;
      frameOwner.clearTimeout(failTimer);
      frameOwner.setTimeout(() => {
        try {
          const frameWindow = frame.contentWindow;
          if (!frameWindow) throw new Error("Print frame window not available.");
          frameOwner.focus();
          frameWindow.focus();
          frameWindow.print();
          frameOwner.setTimeout(() => {
            URL.revokeObjectURL(objectUrl);
            if (!printWindow) frame.remove();
          }, PRINT_CLEANUP_DELAY_MS);
        } catch (error) {
          URL.revokeObjectURL(objectUrl);
          if (!printWindow) frame.remove();
          closeReservedPersonDocumentPrintWindow(printWindow);
          console.error("Druckdialog konnte nicht geoeffnet werden", documentContext, error);
          onPrintFailed?.();
        }
      }, PRINT_LAYOUT_SETTLE_MS);
    };

    frame.src = objectUrl;
    return true;
  } catch (error) {
    closeReservedPersonDocumentPrintWindow(printWindow);
    // Sammelt alles vor dem Druckrahmen: Abruf des Dokuments, Fehler-Payload statt Datei,
    // fehlender Rahmen. Der Toast allein sagt nicht, welcher Fall eingetreten ist.
    console.error("Dokument konnte nicht gedruckt werden", { url, personId, documentId }, error);
    onPrintFailed?.();
    return false;
  }
}

function createHiddenPrintFrame(ownerDocument: Document): HTMLIFrameElement {
  const frame = ownerDocument.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.style.opacity = "0";
  ownerDocument.body.appendChild(frame);
  return frame;
}

async function loadPrintableDocumentBlob(
  url: string,
  loadDocumentBlob: (url: string) => Promise<Blob>
): Promise<Blob> {
  const blob = await loadDocumentBlob(url);

  const contentType = String(blob.type || "").toLowerCase();
  if (contentType.includes("application/json") || contentType.startsWith("text/")) {
    const text = (await blob.text()).trim();
    if (text.startsWith("{") || text.startsWith("[") || text.includes("\"message\"")) {
      throw new Error("Printable document endpoint returned an API error payload.");
    }
  }

  return blob;
}
function writePrintShell(target: ReservedPersonDocumentPrintWindow, preparingText?: string): void {
  const label = preparingText || "Preparing print...";
  target.document.open();
  target.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(label)}</title>
  <style>
    html, body { margin: 0; width: 100%; height: 100%; background: #fff; }
    body { font-family: Arial, sans-serif; color: #1f2933; }
    .status { position: fixed; inset: 0; display: grid; place-items: center; font-size: 14px; }
    iframe { position: fixed; inset: 0; width: 100%; height: 100%; border: 0; background: #fff; }
  </style>
</head>
<body>
  <div class="status">${escapeHtml(label)}</div>
  <iframe id="person-document-print-frame" title="${escapeHtml(label)}"></iframe>
</body>
</html>`);
  target.document.close();
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
